import { useEffect, useRef, useState } from 'react';

import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useAnalysisProfileQuery } from '../api/useAnalysisProfileQuery';
import { useAnalysisResultsQuery } from '../api/useAnalysisResultsQuery';

/**
 * True from the moment the profile leaves PROCESSING until the results refetch
 * that follows it lands. The profile poll reports the new status first, so until
 * the results response arrives the cached results still describe the previous
 * state (its stale flag, placeholder cells).
 *
 * Tracks the transition and the results' `dataUpdatedAt` instead of comparing
 * statuses: after a re-process the cached results carry the previous run's
 * terminal status, which already equals the profile's new one.
 */
export const useResultsBehindProfile = (): boolean => {
  const { projectId, analysisProfileId } = useAnalysisRouteParams();
  const resultsQuery = useAnalysisResultsQuery({ projectId, analysisProfileId });
  const { data: profileEnvelope } = useAnalysisProfileQuery({ projectId, analysisProfileId });

  const profileStatus = profileEnvelope?.data.latestStatus;
  const resultsUpdatedAt = resultsQuery.dataUpdatedAt;

  // `dataUpdatedAt` of the results cached when the run finished; the results are
  // behind until a newer response replaces them.
  const [updatedAtAtRunEnd, setUpdatedAtAtRunEnd] = useState<number | null>(null);
  const previousStatus = useRef(profileStatus);
  useEffect(() => {
    if (previousStatus.current === 'PROCESSING' && profileStatus !== 'PROCESSING') {
      setUpdatedAtAtRunEnd(resultsUpdatedAt);
    }
    previousStatus.current = profileStatus;
  }, [profileStatus, resultsUpdatedAt]);

  // `isFetching` bounds the flag if the refetch errors (data never advances), so
  // a failed refetch doesn't hold the loading state forever.
  return (
    updatedAtAtRunEnd !== null && resultsUpdatedAt === updatedAtAtRunEnd && resultsQuery.isFetching
  );
};
